import { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Users, CheckCircle, Clock, DollarSign, UserPlus, 
  Phone, Mail, Home, Shield, TrendingUp, Briefcase,
  Edit2, CreditCard, Copy, Link2, ExternalLink, Trash2, UserX, Download, XSquare
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Helper to get token from localStorage
const getToken = () => localStorage.getItem('token');

// Get the referrer signup link
const getReferrerLink = () => {
  const baseUrl = window.location.origin;
  return `${baseUrl}/referrer/login`;
};

const statusColors = {
  'Received': 'bg-blue-100 text-blue-800',
  'Assigned to Agent': 'bg-purple-100 text-purple-800',
  'Re-assigned': 'bg-indigo-100 text-indigo-800',
  'Contacted': 'bg-amber-100 text-amber-800',
  'Appointment Set': 'bg-cyan-100 text-cyan-800',
  'Success': 'bg-emerald-100 text-emerald-800',
  'Did not proceed': 'bg-red-100 text-red-800',
  'Paid': 'bg-green-100 text-green-800'
};

const businessTypeIcons = {
  'Mortgage': Home,
  'Insurance': Shield,
  'Investments': TrendingUp
};

export default function ReferralLeads({ division = null }) {
  // division: null = show all (portal), 'mortgage' = mortgage division, 'insurance' = insurance division
  const { user } = useAuth();
  const [referrals, setReferrals] = useState([]);
  const [referrers, setReferrers] = useState([]);
  const [mortgageAgents, setMortgageAgents] = useState([]);
  const [insuranceAgents, setInsuranceAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('referrals');
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [assignData, setAssignData] = useState({ agent_id: '', agent_type: 'mortgage' });
  const [statusData, setStatusData] = useState({ status: '', notes: '' });
  
  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [bulkStatus, setBulkStatus] = useState('');

  const isAdmin = user?.role === 'admin';
  const isPortalView = division === null;

  useEffect(() => {
    fetchData();
  }, [division]);

  const fetchData = async () => {
    const token = getToken();
    try {
      const divisionParam = division ? `?division=${division}` : '';
      const requests = [
        axios.get(`${API}/api/referrals${divisionParam}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/agents`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/insurance/agents`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ];
      
      // Only fetch referrers in portal view (admin)
      if (!division) {
        requests.push(
          axios.get(`${API}/api/referrers/all`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
        );
      }
      
      const [referralsRes, mortgageRes, insuranceRes, referrersRes] = await Promise.all(requests);
      setReferrals(referralsRes.data);
      setMortgageAgents(mortgageRes.data);
      setInsuranceAgents(insuranceRes.data);
      if (referrersRes) {
        setReferrers(referrersRes.data);
      }
    } catch (error) {
      toast.error('Failed to load referrals');
    } finally {
      setLoading(false);
    }
  };

  // Bulk selection handlers
  const toggleSelectAll = () => {
    if (selectedIds.size === referrals.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(referrals.map(r => r.id)));
    }
  };

  const toggleSelect = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleBulkStatusUpdate = async () => {
    if (!bulkStatus || selectedIds.size === 0) return;
    
    const token = getToken();
    try {
      const updatePromises = Array.from(selectedIds).map(id =>
        axios.put(`${API}/api/referrals/${id}/status`, 
          { status: bulkStatus, notes: '' },
          { headers: { Authorization: `Bearer ${token}` } }
        )
      );
      await Promise.all(updatePromises);
      toast.success(`${selectedIds.size} referral(s) updated to "${bulkStatus}"`);
      setSelectedIds(new Set());
      setShowBulkStatusModal(false);
      setBulkStatus('');
      fetchData();
    } catch (error) {
      toast.error('Failed to update some referrals');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    
    const token = getToken();
    try {
      const deletePromises = Array.from(selectedIds).map(id =>
        axios.delete(`${API}/api/referrals/${id}`, 
          { headers: { Authorization: `Bearer ${token}` } }
        )
      );
      await Promise.all(deletePromises);
      toast.success(`${selectedIds.size} referral(s) deleted`);
      setSelectedIds(new Set());
      setShowBulkDeleteConfirm(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete some referrals');
    }
  };

  const handleBulkExport = () => {
    const selectedData = referrals.filter(r => selectedIds.has(r.id));
    if (selectedData.length === 0) {
      toast.error('No records selected for export');
      return;
    }
    
    const exportData = selectedData.map(r => ({
      'Referrer': r.referrer_name,
      'Lead Name': r.referral_name,
      'Lead Email': r.referral_email,
      'Lead Phone': r.referral_phone,
      'Business Type': r.business_type,
      'Status': r.status,
      'Assigned Agent': r.assigned_agent_name || '',
      'Fee Amount': r.referral_fee_amount,
      'Fee Paid': r.referral_fee_paid ? 'Yes' : 'No',
      'Created At': r.created_at
    }));
    
    const headers = Object.keys(exportData[0]);
    const csvContent = [
      headers.join(','),
      ...exportData.map(row => headers.map(h => {
        const val = row[h];
        if (typeof val === 'string' && val.includes(',')) return `"${val}"`;
        return val;
      }).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `referrals_export_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`${selectedData.length} referral(s) exported`);
  };

  const handleDeleteReferrer = async (referrerId, referrerName) => {
    if (!window.confirm(`Are you sure you want to delete referrer "${referrerName}"? This will also delete all their referrals. This action cannot be undone.`)) {
      return;
    }

    const token = getToken();
    try {
      await axios.delete(`${API}/api/referrers/${referrerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Referrer "${referrerName}" deleted successfully`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete referrer');
    }
  };

  const handleAssignAgent = async () => {
    if (!assignData.agent_id) {
      toast.error('Please select an agent');
      return;
    }

    const token = getToken();
    try {
      await axios.put(
        `${API}/api/referrals/${selectedReferral.id}/assign?agent_id=${assignData.agent_id}&agent_type=${assignData.agent_type}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Agent assigned successfully');
      setShowAssignModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign agent');
    }
  };

  const handleUpdateStatus = async () => {
    if (!statusData.status) {
      toast.error('Please select a status');
      return;
    }

    const token = getToken();
    try {
      await axios.put(
        `${API}/api/referrals/${selectedReferral.id}/status?status=${encodeURIComponent(statusData.status)}${statusData.notes ? `&notes=${encodeURIComponent(statusData.notes)}` : ''}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Status updated successfully');
      setShowStatusModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleMarkPaid = async (referralId) => {
    if (!window.confirm('Mark this referral fee as paid ($200 via Interac)? This will notify the referrer.')) {
      return;
    }

    const token = getToken();
    try {
      await axios.put(
        `${API}/api/referrals/${referralId}/mark-paid`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Referral fee marked as paid. Referrer notified.');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as paid');
    }
  };

  const stats = {
    total: referrals.length,
    received: referrals.filter(r => r.status === 'Received').length,
    inProgress: referrals.filter(r => ['Assigned to Agent', 'Contacted', 'Appointment Set'].includes(r.status)).length,
    successful: referrals.filter(r => r.status === 'Success').length,
    pendingPayment: referrals.filter(r => r.status === 'Success' && !r.referral_fee_paid).length,
    totalPaid: referrals.filter(r => r.referral_fee_paid).reduce((sum, r) => sum + r.referral_fee_amount, 0)
  };

  const copyReferralLink = () => {
    const link = getReferrerLink();
    
    // Try modern clipboard API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(link).then(() => {
        toast.success('Referral link copied to clipboard!');
      }).catch(() => {
        // Fallback to older method
        fallbackCopy(link);
      });
    } else {
      // Fallback for browsers without clipboard API
      fallbackCopy(link);
    }
  };

  const fallbackCopy = (text) => {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      textArea.style.top = '-9999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        toast.success('Referral link copied to clipboard!');
      } else {
        toast.error('Failed to copy link. Please copy manually.');
      }
    } catch (err) {
      toast.error('Failed to copy link. Please copy manually.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {isPortalView ? 'All Referral Leads' : `${division === 'mortgage' ? 'Mortgage' : 'Insurance & Investment'} Referrals`}
          </h1>
          <p className="text-slate-500">
            {isPortalView 
              ? 'Manage and assign all referrals from partners' 
              : `Referrals assigned to ${division === 'mortgage' ? 'Mortgage' : 'Insurance & Investment'} agents`}
          </p>
        </div>
        
        {/* Bulk Actions - Show when items selected */}
        {selectedIds.size > 0 && isAdmin && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handleBulkExport}
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            >
              <Download className="w-4 h-4 mr-2" /> Export ({selectedIds.size})
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowBulkStatusModal(true)}
              className="border-blue-300 text-blue-700 hover:bg-blue-50"
            >
              <Edit2 className="w-4 h-4 mr-2" /> Update Status ({selectedIds.size})
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowBulkDeleteConfirm(true)}
              className="border-red-300 text-red-700 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Delete ({selectedIds.size})
            </Button>
            <Button
              variant="ghost"
              onClick={() => setSelectedIds(new Set())}
              className="text-slate-500"
            >
              <XSquare className="w-4 h-4 mr-2" /> Clear
            </Button>
          </div>
        )}
        
        {/* Copy Referral Link Button - Only in Portal View */}
        {isPortalView && isAdmin && selectedIds.size === 0 && (
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-lg border">
              <Link2 className="w-4 h-4 text-slate-500" />
              <span className="text-sm text-slate-600 font-mono max-w-[200px] truncate">
                {getReferrerLink()}
              </span>
            </div>
            <Button 
              onClick={copyReferralLink}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Copy className="w-4 h-4 mr-2" />
              Copy Link
            </Button>
            <Button 
              variant="outline"
              onClick={() => window.open(getReferrerLink(), '_blank')}
            >
              <ExternalLink className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-xs text-slate-500">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.received}</p>
                <p className="text-xs text-slate-500">New</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <UserPlus className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.inProgress}</p>
                <p className="text-xs text-slate-500">In Progress</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.successful}</p>
                <p className="text-xs text-slate-500">Successful</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">${stats.totalPaid}</p>
                <p className="text-xs text-slate-500">Paid Out</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for Referrals and Referrers (Portal View Only) */}
      {isPortalView && isAdmin ? (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="referrals">Referrals ({referrals.length})</TabsTrigger>
            <TabsTrigger value="referrers">Referrer Accounts ({referrers.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="referrals">
            {/* Referrals Table */}
            <Card>
              <CardHeader>
                <CardTitle>All Referrals</CardTitle>
              </CardHeader>
              <CardContent>
                {referrals.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-700">No Referrals Yet</h3>
                    <p className="text-slate-500">Referrals from partners will appear here</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          {isAdmin && (
                            <TableHead className="w-[40px]">
                              <Checkbox
                                checked={selectedIds.size === referrals.length && referrals.length > 0}
                                onCheckedChange={toggleSelectAll}
                              />
                            </TableHead>
                          )}
                          <TableHead>Date</TableHead>
                          <TableHead>Referrer</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Lead</TableHead>
                          <TableHead>Contact</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Agent</TableHead>
                          <TableHead>Fee</TableHead>
                          <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {referrals.map((referral) => {
                    const TypeIcon = businessTypeIcons[referral.business_type] || Briefcase;
                    return (
                      <TableRow key={referral.id} className={selectedIds.has(referral.id) ? 'bg-blue-50' : ''}>
                        {isAdmin && (
                          <TableCell>
                            <Checkbox
                              checked={selectedIds.has(referral.id)}
                              onCheckedChange={() => toggleSelect(referral.id)}
                            />
                          </TableCell>
                        )}
                        <TableCell className="text-sm text-slate-500">
                          {new Date(referral.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{referral.referrer_name}</p>
                            <p className="text-xs text-slate-500">{referral.referrer_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="gap-1">
                            <TypeIcon className="w-3 h-3" />
                            {referral.business_type}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <p className="font-medium">{referral.referral_name}</p>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p className="flex items-center gap-1 text-slate-600">
                              <Mail className="w-3 h-3" />
                              {referral.referral_email}
                            </p>
                            <p className="flex items-center gap-1 text-slate-600">
                              <Phone className="w-3 h-3" />
                              {referral.referral_phone}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={statusColors[referral.status]}>
                            {referral.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {referral.assigned_agent_name || (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {referral.referral_fee_paid ? (
                            <Badge className="bg-green-100 text-green-800">Paid</Badge>
                          ) : referral.status === 'Success' ? (
                            <Badge className="bg-amber-100 text-amber-800">Pending</Badge>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {isAdmin && isPortalView && (
                              <Button
                                size="sm"
                                variant="outline"
                                title={referral.assigned_agent_id ? "Re-assign Agent" : "Assign Agent"}
                                onClick={() => {
                                  setSelectedReferral(referral);
                                  setAssignData({ 
                                    agent_id: referral.assigned_agent_id || '', 
                                    agent_type: referral.business_type === 'Mortgage' ? 'mortgage' : 'insurance'
                                  });
                                  setShowAssignModal(true);
                                }}
                              >
                                <UserPlus className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedReferral(referral);
                                setStatusData({ status: referral.status, notes: referral.notes || '' });
                                setShowStatusModal(true);
                              }}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            {isAdmin && (referral.status === 'Success' || referral.status === 'Paid') && !referral.referral_fee_paid && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-green-600 hover:text-green-700"
                                onClick={() => handleMarkPaid(referral.id)}
                              >
                                <CreditCard className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
          </TabsContent>

          {/* Referrers Tab */}
          <TabsContent value="referrers">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserX className="w-5 h-5" />
                  Referrer Accounts
                </CardTitle>
              </CardHeader>
              <CardContent>
                {referrers.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-700">No Referrers Yet</h3>
                    <p className="text-slate-500">Referrer accounts will appear here when they sign up</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Phone</TableHead>
                          <TableHead className="text-center">Referrals</TableHead>
                          <TableHead className="text-center">Successful</TableHead>
                          <TableHead className="text-right">Earnings</TableHead>
                          <TableHead>Joined</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {referrers.map((referrer) => (
                          <TableRow key={referrer.id}>
                            <TableCell className="font-medium">{referrer.name}</TableCell>
                            <TableCell>
                              <span className="flex items-center gap-1 text-slate-600">
                                <Mail className="w-3 h-3" />
                                {referrer.email}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span className="flex items-center gap-1 text-slate-600">
                                <Phone className="w-3 h-3" />
                                {referrer.phone}
                              </span>
                            </TableCell>
                            <TableCell className="text-center">{referrer.referral_count}</TableCell>
                            <TableCell className="text-center">
                              <Badge className="bg-emerald-100 text-emerald-800">
                                {referrer.successful_referrals}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right font-bold text-emerald-600">
                              ${referrer.total_earnings}
                            </TableCell>
                            <TableCell className="text-sm text-slate-500">
                              {new Date(referrer.created_at).toLocaleDateString()}
                            </TableCell>
                            <TableCell>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDeleteReferrer(referrer.id, referrer.name)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      ) : (
        /* Non-portal view - just show referrals table */
        <Card>
          <CardHeader>
            <CardTitle>Referrals</CardTitle>
          </CardHeader>
          <CardContent>
            {referrals.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700">No Referrals Yet</h3>
                <p className="text-slate-500">Referrals assigned to your division will appear here</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Referrer</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Lead</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Agent</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {referrals.map((referral) => {
                      const TypeIcon = businessTypeIcons[referral.business_type] || Briefcase;
                      return (
                        <TableRow key={referral.id}>
                          <TableCell className="text-sm text-slate-500">
                            {new Date(referral.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{referral.referrer_name}</p>
                              <p className="text-xs text-slate-500">{referral.referrer_email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="gap-1">
                              <TypeIcon className="w-3 h-3" />
                              {referral.business_type}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <p className="font-medium">{referral.referral_name}</p>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <p className="flex items-center gap-1 text-slate-600">
                                <Mail className="w-3 h-3" />
                                {referral.referral_email}
                              </p>
                              <p className="flex items-center gap-1 text-slate-600">
                                <Phone className="w-3 h-3" />
                                {referral.referral_phone}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={statusColors[referral.status]}>
                              {referral.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {referral.assigned_agent_name || <span className="text-slate-400">-</span>}
                          </TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedReferral(referral);
                                setStatusData({ status: referral.status, notes: referral.notes || '' });
                                setShowStatusModal(true);
                              }}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Assign Agent Modal */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedReferral?.assigned_agent_id ? 'Re-assign Agent' : 'Assign Agent to Referral'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedReferral && (
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="font-medium">{selectedReferral.referral_name}</p>
                <p className="text-sm text-slate-500">{selectedReferral.business_type}</p>
                {selectedReferral.assigned_agent_name && (
                  <p className="text-sm text-amber-600 mt-1">
                    Currently assigned to: {selectedReferral.assigned_agent_name}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-medium">Agent Type</label>
              <Select 
                value={assignData.agent_type} 
                onValueChange={(value) => setAssignData({ ...assignData, agent_type: value, agent_id: '' })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mortgage">Mortgage Agent</SelectItem>
                  <SelectItem value="insurance">Insurance & Investment Agent</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Select Agent</label>
              <Select 
                value={assignData.agent_id} 
                onValueChange={(value) => setAssignData({ ...assignData, agent_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose an agent..." />
                </SelectTrigger>
                <SelectContent>
                  {(assignData.agent_type === 'mortgage' ? mortgageAgents : insuranceAgents).map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      {agent.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button onClick={handleAssignAgent} className="w-full">
              {selectedReferral?.assigned_agent_id ? 'Re-assign Agent' : 'Assign Agent'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Update Status Modal */}
      <Dialog open={showStatusModal} onOpenChange={setShowStatusModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Referral Status</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedReferral && (
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="font-medium">{selectedReferral.referral_name}</p>
                <p className="text-sm text-slate-500">Current: {selectedReferral.status}</p>
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-medium">Status</label>
              <Select 
                value={statusData.status} 
                onValueChange={(value) => setStatusData({ ...statusData, status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Received">Received</SelectItem>
                  <SelectItem value="Assigned to Agent">Assigned to Agent</SelectItem>
                  <SelectItem value="Re-assigned">Re-assigned</SelectItem>
                  <SelectItem value="Contacted">Contacted</SelectItem>
                  <SelectItem value="Appointment Set">Appointment Set</SelectItem>
                  <SelectItem value="Success">Success</SelectItem>
                  <SelectItem value="Did not proceed">Did not proceed</SelectItem>
                  <SelectItem value="Paid">Paid</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Notes</label>
              <Textarea
                value={statusData.notes}
                onChange={(e) => setStatusData({ ...statusData, notes: e.target.value })}
                placeholder="Add notes about this referral..."
                rows={3}
              />
            </div>

            <Button onClick={handleUpdateStatus} className="w-full">
              Update Status
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Status Update Modal */}
      <Dialog open={showBulkStatusModal} onOpenChange={setShowBulkStatusModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Status for {selectedIds.size} Referral(s)</DialogTitle>
            <DialogDescription>
              Select a new status to apply to all selected referrals.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <Select value={bulkStatus} onValueChange={setBulkStatus}>
              <SelectTrigger>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Received">Received</SelectItem>
                <SelectItem value="Assigned to Agent">Assigned to Agent</SelectItem>
                <SelectItem value="Re-assigned">Re-assigned</SelectItem>
                <SelectItem value="Contacted">Contacted</SelectItem>
                <SelectItem value="Appointment Set">Appointment Set</SelectItem>
                <SelectItem value="Success">Success</SelectItem>
                <SelectItem value="Did not proceed">Did not proceed</SelectItem>
                <SelectItem value="Paid">Paid</SelectItem>
              </SelectContent>
            </Select>
            <Button 
              onClick={handleBulkStatusUpdate} 
              className="w-full"
              disabled={!bulkStatus}
            >
              Update {selectedIds.size} Referral(s)
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Modal */}
      <Dialog open={showBulkDeleteConfirm} onOpenChange={setShowBulkDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Bulk Delete</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedIds.size} selected referral(s)? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleBulkDelete}>
              <Trash2 className="w-4 h-4 mr-2" />
              Delete {selectedIds.size} Referral(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
